'use client'

import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { usePathname, useRouter } from '@/i18n/navigation'
import { Locale } from '@/types'

const LOCALES: { code: Locale; label: string }[] = [
  { code: Locale.English, label: 'English' },
  { code: Locale.Shqip, label: 'Shqip' },
]
/**
 * Component: Dropdown do select site language
 *
 * @param className - any custom classname
 * @returns LanguageSelector dropdown
 */
export const LanguageSelector = ({ className = '' }: { className?: string }) => {
  const t = useTranslations('Header')
  const locale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = LOCALES.find(item => item.code === locale) ?? LOCALES[0]

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const switchLocale = (nextLocale: Locale) => {
    router.replace(pathname, { locale: nextLocale })
    setOpen(false)
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-gray-300 text-sm font-medium hover:color-primary transition-colors"
        aria-label={'Select language'}
        aria-expanded={open}>
        <span className="uppercase text-black">{current.code}</span>
        <ChevronDown
          size={14}
          className={`text-black transition-all duration-200 ${open ? 'rotate-180 color-primary' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute top-full right-0 pt-2 z-50">
          <div className="min-w-40 bg-white border border-white/10 rounded-xl py-2 shadow-2xl shadow-black/60">
            {LOCALES.map(item => (
              <button
                key={item.code}
                type="button"
                onClick={() => switchLocale(item.code)}
                className={`block w-full cursor-pointer text-left px-4 py-2.5 text-sm transition-colors ${
                  item.code === locale
                    ? 'color-primary bg-white/10'
                    : 'text-gray-300 hover:color-primary hover:bg-white/5'
                }`}>
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
